import {useEffect, useState} from 'react'

import {
  agentMergeEnrollmentDeadlineMillis,
  agentMergeEnrollmentDisplayState,
  type AgentMergeEnrollmentDisplayState,
} from '../helpers/agent-merge-enrollment-status'
import type {AgentMergeEnrollmentAttempt} from '../page-data/payloads/agent-merge'

/**
 * Derives the enrollment placeholder/banner state for the current attempt, and schedules a
 * single timer to force a re-render exactly when a `pending`/`succeeded` attempt crosses its
 * deadline. Without this, a `pending` placeholder would keep rendering past its deadline until
 * some unrelated re-render happened to occur, since wall-clock time passing is not itself a state
 * change React reacts to.
 */
export function useAgentMergeEnrollmentDisplayState(
  attempt: AgentMergeEnrollmentAttempt | undefined,
): AgentMergeEnrollmentDisplayState | null {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    // Only `pending`/`succeeded` can flip on deadline alone; the other states are already
    // terminal (or terminal-ish, for `unconfirmed`) regardless of wall-clock time.
    if (attempt?.state !== 'pending' && attempt?.state !== 'succeeded') return undefined
    // +1 so the timer fires strictly after the deadline instant. `agentMergeEnrollmentDisplayState`
    // compares with a strict `>`, so a timer scheduled for exactly `remaining` ms would fire with
    // `Date.now() === deadline` and never actually flip the state.
    const remaining = agentMergeEnrollmentDeadlineMillis(attempt) - Date.now() + 1
    // Deferred into the timeout callback (even at a 0ms delay) rather than called directly in the
    // effect body, so a newly-arrived already-expired attempt still resynchronizes `now` without
    // setting state synchronously during the effect.
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(remaining, 0))
    return () => window.clearTimeout(timer)
  }, [attempt])

  return agentMergeEnrollmentDisplayState(attempt, now)
}
